import React from 'react'
import styled from 'styled-components'
import { match } from 'ts-pattern'

import { AsyncStateN } from '@blade/std-lib'

import { AnimateHeight } from '../animations'
import { Drawer } from '../drawer'
import { StatusBox } from '../status-box'

import type { DrawerControlProps } from '../drawer'
import type { IconProps } from '../icons'
import type { Intent } from '../theme'
import type { Error } from '@root/shared'
import type { ChildrenProps, ClassNameProps } from '@root/shared/lib/react'

export type ProgressDrawerProps = {
  state: AsyncStateN.AsyncStateN<Error.UnknownError, unknown>
  title?: React.ReactNode
  description?: React.ReactNode
  intent?: Intent
  icon?: React.FC<IconProps>
} & ClassNameProps &
  DrawerControlProps &
  ChildrenProps

export const ProgressDrawer: React.FC<ProgressDrawerProps> = ({
  state,
  open,
  onOpenChange,
  children,
  title,
  description,
  intent: intentOverride,
  icon: iconOverride,
}) => {
  const intent = intentOverride ?? stateToIntent(state)
  return (
    <Root open={open} onOpenChange={onOpenChange} dismissible={false}>
      <AnimateHeight>
        <Content>
          <StatusContainer>
            <StatusBox status={state} icon={iconOverride} />
          </StatusContainer>
          <Title $intent={intent}>{title}</Title>
          <Description>{description}</Description>
          {children}
        </Content>
      </AnimateHeight>
    </Root>
  )
}
const stateToIntent = (
  state: AsyncStateN.AsyncStateN<Error.UnknownError, unknown>,
) => {
  return match(state)
    .returnType<Intent>()
    .when(AsyncStateN.isError, (_) => 'error')
    .when(AsyncStateN.isSuccess, (_) => 'success')
    .otherwise(() => 'neutral')
}

const Root = styled(Drawer)``

const Content = styled.div`
  display: grid;
  padding-inline: 12px;
  padding-block: 8px var(--safe-bottom);
`

const StatusContainer = styled.div`
  display: grid;
  justify-items: center;
  margin-bottom: 12px;
`

const Title = styled.div<{ $intent?: Intent | undefined }>`
  text-align: center;
  color: ${({ $intent }) => {
    if ($intent === 'error') {
      return 'var(--error-9)'
    }
    if ($intent === 'success') {
      return 'var(--success-9)'
    }
    return 'var(--text-12)'
  }};

  font-size: 16px;
  font-weight: 700;
  line-height: 20px;
  margin-bottom: 12px;
`

const Description = styled.div`
  color: var(--text-9);
  text-align: center;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  margin-bottom: 32px;
`
